import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRbac } from "@/hooks/useRbac";
import { normalizeRole } from "@/lib/rbac";
import { useModalities } from "@/contexts/ModalitiesContext";
import { useUploadProgress } from "@/contexts/UploadProgressContext";
import { needsAddStudyMismatchReview } from "@/lib/addStudyMismatch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StudyDiscussion from "@/components/StudyDiscussion";
import StudyReportsList from "@/components/StudyReportsList";
import ImportReportDialog from "@/components/ImportReportDialog";
import ShareStudyDialog from "@/components/ShareStudyDialog";
import SharePatientDialog from "@/components/SharePatientDialog";
import PatientTags from "@/components/PatientTags";
import EditPatientDialog from "@/components/EditPatientDialog";
import OfficeDocumentViewerModal from "@/components/OfficeDocumentViewerModal";
import UploadPage from "@/pages/Upload";
import {
  ArrowLeft, Eye, Clock,
  Share2, MessageSquare, MessageCircle, Download, Loader2,
  Upload, UserPlus, ShieldAlert, FileDown, Pencil, Trash2,
} from "lucide-react";
import { getModalityChartColor, getModalityBadgeClass, isOfficeDocumentFile, isOtherModality } from "@/lib/modalities";
import { isEchoFormModality, isFormOnlyStudy } from "@/lib/echoMeasurements";
import { formatApiErrorDetail, parseAxiosErrorDetail } from "@/lib/api";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import { formatPatientAnatomyTypes } from "@/lib/anatomyTypes";
import { PageHeaderActionsLeading } from "@/contexts/PageHeaderContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STUDIES_PANEL_HEIGHT = "h-[min(500px,calc(100vh-19rem))]";

const isStudyPendingDeletion = (study) => {
  if (!study) return false;
  if (study.status === "pending_deletion") return true;
  return Boolean(study.pending_deletion_at);
};

const isPatientPendingDeletion = (patient) => {
  if (!patient) return false;
  if (patient.status === "pending_deletion") return true;
  return Boolean(patient.pending_deletion_at);
};

const AUDIT_ACTION_ICONS = {
  upload: Upload,
  download: Download,
  view: Eye,
  share: Share2,
  create: UserPlus,
  anonymize: ShieldAlert,
  update: UserPlus,
  seed: FileDown,
  login: UserPlus,
};

const AUDIT_ACTION_COLORS = {
  upload: "bg-sky-50 text-sky-700",
  download: "bg-teal-50 text-teal-700",
  view: "bg-amber-50 text-amber-700",
  share: "bg-violet-50 text-violet-700",
  create: "bg-cyan-50 text-cyan-700",
  anonymize: "bg-red-50 text-red-700",
  update: "bg-teal-50 text-teal-700",
  seed: "bg-slate-100 text-slate-600",
  login: "bg-indigo-50 text-indigo-700",
};

const formatAuditTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isMissingValue = (value) => {
  if (value === null || value === undefined) return true;
  const text = String(value).trim();
  if (!text) return true;
  const lower = text.toLowerCase();
  return lower === "na" || lower === "n/a" || lower === "null" || lower === "none";
};

const formatAgeDob = (age, dateOfBirth) => {
  const parts = [];
  if (!isMissingValue(age)) parts.push(`${age} yrs`);
  if (!isMissingValue(dateOfBirth)) parts.push(dateOfBirth);
  return parts.length ? parts.join(" · ") : null;
};

export default function PatientDetail() {
  const { uhid } = useParams();
  const navigate = useNavigate();
  const {
    role,
    canManagePatients,
    canUpload,
    canShare,
    canViewStudies,
    canAnonymizePatients,
    canExportCsv,
    permissions,
  } = useRbac();
  const canDownload = canExportCsv || permissions?.includes("download");
  const canAnonymize = canAnonymizePatients;
  const isSuperAdmin = normalizeRole(role) === "super_admin";
  const { getModality } = useModalities();
  const {
    pendingMismatchFileId,
    pendingAddStudyFileId,
    clearPendingAddStudyFile,
    files: uploadFiles,
    registerEmbeddedUploadCloser,
    openLockedPatientMismatchReview,
  } = useUploadProgress();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discussionStudy, setDiscussionStudy] = useState(null);
  const [importStudy, setImportStudy] = useState(null);
  const [shareStudy, setShareStudy] = useState(null);
  const [sharePatientOpen, setSharePatientOpen] = useState(false);
  const [documentPreviewFile, setDocumentPreviewFile] = useState(null);
  const [downloadingStudyId, setDownloadingStudyId] = useState(null);
  const [anonymizeUpdating, setAnonymizeUpdating] = useState(false);
  const [addStudyOpen, setAddStudyOpen] = useState(false);
  /** File id to claim when reopening Add Study from the tray "Continue" action. */
  const [finishAddStudyFileId, setFinishAddStudyFileId] = useState(null);
  /** Mount Upload invisibly so tray Mismatch can open the dialog without Add Study /upload UI. */
  const [mismatchReviewHostOpen, setMismatchReviewHostOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [studyToDelete, setStudyToDelete] = useState(null);
  const [deletingStudy, setDeletingStudy] = useState(false);
  const [echoFormStudy, setEchoFormStudy] = useState(null);
  const [remarksStudy, setRemarksStudy] = useState(null);
  const [remarksDraft, setRemarksDraft] = useState("");
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [timelineLogs, setTimelineLogs] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);

  const refreshPatient = useCallback(async () => {
    if (!uhid) return;
    const res = await axios.get(`${API}/patients/${uhid}`);
    setData(res.data);
  }, [uhid]);

  const handleCloseAddStudyHost = useCallback(() => {
    setAddStudyOpen(false);
    setMismatchReviewHostOpen(false);
    setFinishAddStudyFileId(null);
    // Sheet onOpenChange may not fire for programmatic close — refresh studies after save/cancel.
    refreshPatient().catch(() => {});
  }, [refreshPatient]);

  useEffect(() => registerEmbeddedUploadCloser(handleCloseAddStudyHost), [
    registerEmbeddedUploadCloser,
    handleCloseAddStudyHost,
  ]);

  // Tray "Continue" / ready-to-save — reopen Add Study on this patient to finish upload.
  useEffect(() => {
    if (!pendingAddStudyFileId || !canUpload) return;
    const pendingFile = uploadFiles.find((f) => f.id === pendingAddStudyFileId);
    if (!pendingFile || pendingFile.status !== "extracted") return;
    // Extracted a different/existing patient — open the exists popup, not Add Study.
    if (pendingFile.patient_mismatch || needsAddStudyMismatchReview(pendingFile)) {
      clearPendingAddStudyFile();
      openLockedPatientMismatchReview(pendingAddStudyFileId);
      return;
    }
    const isAddStudyFile = (
      pendingFile.origin === "add_study"
      || pendingFile.queue === "add_study"
      || Boolean(pendingFile.locked_patient_uhid)
    );
    if (!isAddStudyFile) return;
    const targetUhid = String(pendingFile.locked_patient_uhid || "").trim();
    const currentUhid = data?.patient?.uhid ?? uhid;
    if (targetUhid && currentUhid && targetUhid !== currentUhid) return;

    setFinishAddStudyFileId(pendingAddStudyFileId);
    setMismatchReviewHostOpen(false);
    setAddStudyOpen(true);
    clearPendingAddStudyFile();
  }, [
    pendingAddStudyFileId,
    uploadFiles,
    canUpload,
    uhid,
    data?.patient?.uhid,
    clearPendingAddStudyFile,
    openLockedPatientMismatchReview,
  ]);

  // Tray Mismatch — mount a hidden Upload host on this patient page (popup only, no /upload).
  // /upload-origin "exists" reviews must stay on /upload — never open the Add Study host.
  useEffect(() => {
    if (!pendingMismatchFileId || !canUpload) return;
    const pendingFile = uploadFiles.find((f) => f.id === pendingMismatchFileId);
    if (!pendingFile) return;
    if (
      !pendingFile.patient_mismatch
      && !needsAddStudyMismatchReview(pendingFile)
    ) {
      return;
    }
    if (pendingFile.mismatch_kind === "exists" || pendingFile.origin === "upload") return;
    const isAddStudyReview = (
      pendingFile.origin === "add_study"
      || pendingFile.queue === "add_study"
      || Boolean(pendingFile.locked_patient_uhid)
      || pendingFile.mismatch_kind === "add_study_exists"
      || pendingFile.mismatch_kind === "add_study_new"
      || pendingFile.mismatch_kind === "locked"
    );
    if (!isAddStudyReview) return;
    // Add Study reviews belong on the locked patient page, not the extracted existing match.
    const targetUhid = (
      pendingFile.locked_patient_uhid?.trim()
      || pendingFile.mismatch_patient_uhid?.trim()
      || ""
    );
    const currentUhid = data?.patient?.uhid ?? uhid;
    if (targetUhid && currentUhid && targetUhid !== currentUhid) return;
    setAddStudyOpen(false);
    setMismatchReviewHostOpen(true);
  }, [pendingMismatchFileId, uploadFiles, canUpload, uhid, data?.patient?.uhid]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/patients/${uhid}`);
        setData(res.data);
      } catch {
        toast.error("Patient not found");
        navigate("/patients");
      }
      setLoading(false);
    };
    fetch();
  }, [uhid, navigate]);

  useEffect(() => {
    if (!uhid) return undefined;
    let cancelled = false;
    setTimelineLoading(true);

    axios
      .get(`${API}/audit`, {
        params: { patient_uhid: uhid, limit: 100 },
      })
      .then((res) => {
        if (cancelled) return;
        const logs = Array.isArray(res.data?.logs) ? res.data.logs : [];
        setTimelineLogs(logs);
      })
      .catch(() => {
        if (cancelled) return;
        setTimelineLogs([]);
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false);
      });

    return () => { cancelled = true; };
  }, [uhid]);

  const handleAnonymizeChange = async (nextAnonymized) => {
    if (anonymizeUpdating || !data?.patient) return;

    const previous = Boolean(data.patient.anonymized);
    setAnonymizeUpdating(true);
    setData((prev) => ({
      ...prev,
      patient: { ...prev.patient, anonymized: nextAnonymized },
    }));

    try {
      await axios.put(`${API}/patients/${uhid}/${nextAnonymized ? "anonymize" : "unanonymize"}`);
      await refreshPatient();
      toast.success(nextAnonymized ? "Patient anonymized" : "Patient unanonymized");
    } catch {
      setData((prev) => ({
        ...prev,
        patient: { ...prev.patient, anonymized: previous },
      }));
      toast.error(nextAnonymized ? "Failed to anonymize" : "Failed to unanonymize");
    } finally {
      setAnonymizeUpdating(false);
    }
  };

  const handleTagsUpdated = (updated) => {
    const tags = Array.isArray(updated) ? updated : (updated?.tags ?? []);
    setData((prev) => ({
      ...prev,
      patient: { ...prev.patient, tags },
    }));
  };

  const handlePatientSaved = (updated) => {
    if (!updated) {
      refreshPatient().catch(() => {});
      return;
    }
    setData((prev) => ({
      ...prev,
      patient: { ...prev.patient, ...updated },
    }));
  };

  const handleOpenRemarks = (study) => {
    if (!study?.id) return;
    setRemarksStudy(study);
    setRemarksDraft(String(study.remarks ?? ""));
  };

  const handleConfirmDelete = async () => {
    if (!uhid) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/patients/${encodeURIComponent(uhid)}`);
      toast.success("Patient deleted");
      setDeleteOpen(false);
      navigate("/patients");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete patient");
    } finally {
      setDeleting(false);
    }
  };

  const updateStudyInList = (studyId, patch) => {
    setData((prev) => {
      if (!prev?.studies) return prev;
      return {
        ...prev,
        studies: prev.studies.map((s) => (s.id === studyId ? { ...s, ...patch } : s)),
      };
    });
  };

  const handleSaveRemarks = async () => {
    if (!remarksStudy?.id) return;
    setSavingRemarks(true);
    try {
      const res = await axios.put(`${API}/studies/${encodeURIComponent(remarksStudy.id)}`, {
        remarks: remarksDraft,
      });
      updateStudyInList(remarksStudy.id, {
        remarks: res.data?.remarks ?? remarksDraft,
      });
      toast.success("Comment saved");
      setRemarksStudy(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save comment");
    } finally {
      setSavingRemarks(false);
    }
  };

  const removeStudyFromList = (studyId) => {
    setData((prev) => {
      if (!prev?.studies) return prev;
      return {
        ...prev,
        studies: prev.studies.filter((s) => s.id !== studyId),
      };
    });
  };

  const handleConfirmDeleteStudy = async () => {
    if (!studyToDelete?.id) return;
    setDeletingStudy(true);
    try {
      const res = await axios.delete(`${API}/studies/${encodeURIComponent(studyToDelete.id)}`);
      const status = res.data?.status;
      if (status === "pending_deletion") {
        updateStudyInList(studyToDelete.id, {
          status: "pending_deletion",
          pending_deletion_at: new Date().toISOString(),
          pending_files_count: res.data?.files_count ?? studyToDelete.files_count ?? 0,
          pending_reports_count: res.data?.reports_count ?? studyToDelete.reports_count ?? 0,
        });
        toast.success("Deletion requested — awaiting admin approval");
      } else {
        removeStudyFromList(studyToDelete.id);
        toast.success("Study deleted");
      }
      setStudyToDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete study");
    } finally {
      setDeletingStudy(false);
    }
  };

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => navigate("/patients")}
      data-testid="back-to-patients"
      className="gap-1 h-9 px-2"
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="hidden sm:inline">Back</span>
    </Button>
  );

  if (loading) {
    return (
      <>
        <PageHeaderActionsLeading>{backButton}</PageHeaderActionsLeading>
        <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>
      </>
    );
  }
  if (!data) return null;

  const { patient, studies, files = [], reports = [] } = data;
  const patientPendingDeletion = isPatientPendingDeletion(patient);
  const addStudyPatient = patient
    ? {
        ...patient,
        anatomyDetails: patient.anatomyDetails ?? data.anatomyDetails ?? null,
      }
    : patient;
  const currentPatientUhid = patient?.uhid ?? uhid;
  const hasAddStudyBackgroundWork = uploadFiles.some((f) => {
    if (!f || f.status === "completed" || f.status === "error") return false;
    const isAddStudyFile = (
      f.origin === "add_study"
      || f.queue === "add_study"
      || Boolean(f.locked_patient_uhid)
    );
    if (!isAddStudyFile) return false;
    const lockedUhid = String(f.locked_patient_uhid || "").trim();
    return !lockedUhid || lockedUhid === currentPatientUhid;
  });
  const showHiddenAddStudyHost = !addStudyOpen && (
    mismatchReviewHostOpen || hasAddStudyBackgroundWork
  );
  const anatomyLabel = formatPatientAnatomyTypes(addStudyPatient);

  const getReportIdForStudy = (studyId) =>
    reports.find((r) => r.study_id === studyId)?.id ?? null;

  const isPdfStudy = (study) =>
    Boolean(getReportIdForStudy(study.id)) ||
    getModality(study.modality)?.view_mode === "pdf";

  const isDocumentStudy = (study) =>
    getModality(study.modality)?.view_mode === "document"
    || isOtherModality(study.modality);

  const canPreviewStudy = (study) => {
    if (isFormOnlyStudy(study)) return true;
    if (isDocumentStudy(study) || isOtherModality(study.modality)) return true;
    return isPdfStudy(study) || canViewStudies;
  };

  const openStudy = async (study) => {
    if (isFormOnlyStudy(study)) {
      setEchoFormStudy(study);
      return;
    }
    let reportId = getReportIdForStudy(study.id);

    if (!reportId && isPdfStudy(study)) {
      try {
        const res = await axios.get(`${API}/studies/${study.id}/reports`);
        reportId = res.data.reports?.[0]?.id;
      } catch {
        // fall through to viewer
      }
    }

    if (reportId) {
      navigate(`/reports/${reportId}`, {
        state: { from: `/patients/${encodeURIComponent(uhid)}` },
      });
      return;
    }

    if (isDocumentStudy(study) || isOtherModality(study.modality)) {
      const studyFile = files.find((f) => f.study_id === study.id)
        ?? (await axios.get(`${API}/files`, { params: { study_id: study.id, limit: 20 } })
          .then((res) => res.data?.files?.[0])
          .catch(() => null));
      if (!studyFile) {
        toast.error("No file to preview");
        return;
      }
      const fileName = studyFile.original_filename || studyFile.filename || "";
      if (isOfficeDocumentFile(fileName) || studyFile.file_type === "pdf" || fileName.toLowerCase().endsWith(".pdf")) {
        setDocumentPreviewFile(studyFile);
        return;
      }
      if (canViewStudies) {
        navigate(`/viewer/${study.id}`);
        return;
      }
      toast.error("This file cannot be previewed");
      return;
    }

    if (canViewStudies) navigate(`/viewer/${study.id}`);
  };

  const handleDownloadStudy = async (study) => {
    setDownloadingStudyId(study.id);
    try {
      const res = await axios.get(`${API}/files/study/${study.id}/download-all`, {
        responseType: "blob",
      });

      const disposition = res.headers?.["content-disposition"];
      let filename = `${study.description || study.id}_files.zip`.replace(/[^\w.-]+/g, "_");
      const match = disposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      if (match?.[1]) filename = decodeURIComponent(match[1]);

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      const detail = await parseAxiosErrorDetail(err);
      toast.error(detail != null ? formatApiErrorDetail(detail) : "Download failed");
    } finally {
      setDownloadingStudyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="patient-detail-page">
      <PageHeaderActionsLeading>{backButton}</PageHeaderActionsLeading>

      <Card className="animate-fade-in overflow-hidden border-[var(--xl-line)] bg-white" data-testid="patient-info-card">
        <CardContent className="p-0">
          <div className="flex flex-col lg:flex-row">
            <div className="flex-1 min-w-0 p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex-shrink-0 grid place-items-center text-sm sm:text-base font-bold text-white bg-[var(--xl-black)]"
                    aria-hidden
                  >
                    {patientInitials(patient.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                        {patient.name}
                      </h2>
                      {patient.status === "completed" && (
                        <Badge
                          className="gap-1.5 text-[11px] font-medium border-transparent bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                          data-testid="patient-status-completed"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Completed
                        </Badge>
                      )}
                      {patientPendingDeletion && (
                        <Badge
                          className="text-[10px] font-normal border-transparent bg-amber-100 text-amber-800"
                          data-testid="patient-pending-deletion"
                        >
                          Pending deletion
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="text-sm font-mono text-[var(--xl-primary)]">
                        {formatPatientDisplayUhid(patient)}
                      </span>
                      <PatientTags
                        uhid={uhid}
                        tags={patient.tags ?? []}
                        canEdit={canManagePatients}
                        onTagsUpdated={handleTagsUpdated}
                      />
                      {patientPendingDeletion && patient.pending_deletion_by_name && (
                        <span className="text-[11px] text-muted-foreground">
                          Requested by {patient.pending_deletion_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {(canManagePatients || canShare || anatomyLabel) && (
                  <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0 min-w-0">
                    {(canManagePatients || canShare) && (
                      <div className="flex flex-wrap items-center gap-2">
                        {canShare && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            onClick={() => setSharePatientOpen(true)}
                            data-testid="share-patient-btn"
                            aria-label="Share patient"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                            Share
                          </Button>
                        )}
                        {canManagePatients && (
                          <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5 text-xs border-black/80 text-black hover:bg-black/5"
                          onClick={() => setEditOpen(true)}
                          data-testid="edit-patient-btn"
                          aria-label="Edit patient"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </Button>
                        {!patientPendingDeletion && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 text-xs text-red-600 border-red-500 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteOpen(true)}
                            data-testid="delete-patient-btn"
                            aria-label="Delete patient"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </Button>
                        )}
                          </>
                        )}
                      </div>
                    )}
                    {anatomyLabel ? (
                      <div className="min-w-0 text-left sm:text-right" data-testid="patient-anatomy-types">
                        
                        <p className="text-sm font-semibold mt-0.5 break-words">
                          {anatomyLabel}
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <Separator className="my-4" />

              <div className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3">
                <InfoField label="Age" value={formatAgeDob(patient.age)} alwaysShow />
                <InfoField label="Gender" value={patient.gender} alwaysShow />
                <InfoField label="Doctor" value={patient.referring_doctor} alwaysShow />
                {isSuperAdmin && (
                  <InfoField label="Organization" value={patient.organization} alwaysShow />
                )}
                <InfoField label="Hospital" value={patient.institution_name} alwaysShow />
                <div className="min-w-0 text-left">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Anonymize</p>
                  {canAnonymize ? (
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={Boolean(patient.anonymized)}
                        onCheckedChange={handleAnonymizeChange}
                        disabled={anonymizeUpdating}
                        data-testid={patient.anonymized ? "unanonymize-btn" : "anonymize-btn"}
                        aria-label={patient.anonymized ? "Unanonymize patient" : "Anonymize patient"}
                      />
                      <span className="text-sm font-semibold">
                        {patient.anonymized ? "On" : "Off"}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold mt-1">
                      {patient.anonymized ? "On" : "Off"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {patient.status === "completed" && (
              <div
                className="w-full lg:w-[240px] lg:flex-shrink-0 bg-[var(--xl-black)] px-5 py-5"
                data-testid="patient-valve-card"
              >
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--xl-primary)] mb-4">
                  Valve Details
                </p>
                <div className="divide-y divide-white/15">
                  <ValveDetailRow label="Manufacturer" value={patient.valve_manufacturer_category} />
                  <ValveDetailRow label="Valve Type" value={patient.valve_type} />
                  <ValveDetailRow label="Valve Size" value={patient.valve_size} />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        <Card className="flex flex-col min-w-0 lg:col-span-8" data-testid="patient-studies-table">
          <CardHeader className="pb-2 pt-4 flex flex-row items-center justify-between gap-2 shrink-0 min-h-[3.25rem]">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              All Studies ({studies.length})
            </CardTitle>
            {canUpload && (
              <Button
                size="sm"
                className="text-xs"
                onClick={() => setAddStudyOpen(true)}
                data-testid="add-study-btn"
              >
                + Add Study
              </Button>
            )}
          </CardHeader>
          <CardContent className="flex-1 p-0 pb-3 min-h-0">
            <div className={`overflow-auto ${STUDIES_PANEL_HEIGHT}`}>
              <Table className="sticky-table table-fixed w-full min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Modality</TableHead>
                    <TableHead className="w-[8.5rem]">CT Reviewer</TableHead>
                    <TableHead className="w-[6.75rem]">Date</TableHead>
                    <TableHead className="w-14">Files</TableHead>
                    <TableHead className="w-[11.25rem]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {studies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No studies recorded
                      </TableCell>
                    </TableRow>
                  ) : studies.map((s, i) => {
                    const pendingDeletion = isStudyPendingDeletion(s);
                    const ctReviewer = String(s.ct_reviewer ?? "").trim();
                    const formOnly = isFormOnlyStudy(s);
                    const isEchoFormRow = formOnly && isEchoFormModality(s.modality);
                    return (
                    <TableRow key={s.id} data-testid={`study-row-${i}`}>
                      <TableCell>
                        <div className="flex flex-col gap-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                            <Badge className={`text-xs w-fit max-w-full gap-1.5 font-normal border-transparent ${getModalityBadgeClass(s.modality)}`}>
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: getModalityChartColor(s.modality) }}
                                aria-hidden
                              />
                              <span className="truncate">{getModality(s.modality)?.label ?? s.modality}</span>
                            </Badge>
                            {isEchoFormRow && (
                              <Badge
                                variant="outline"
                                className="text-[10px] w-fit shrink-0 font-normal"
                                data-testid={`study-form-label-${i}`}
                              >
                                Form
                              </Badge>
                            )}
                            {s.mismatched === true && (
                              <Badge
                                className="text-[10px] w-fit shrink-0 font-normal border-transparent bg-red-100 text-red-800"
                                data-testid={`study-mismatched-${i}`}
                              >
                                Mismatched
                              </Badge>
                            )}
                            {isOtherModality(s.modality) && (
                              <button
                                type="button"
                                className="inline-flex shrink-0 p-0.5 rounded-md text-muted-foreground hover:bg-secondary"
                                title="View comment"
                                aria-label="View comment"
                                data-testid={`study-other-comment-${i}`}
                                onClick={() => handleOpenRemarks(s)}
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {pendingDeletion && (
                              <Badge
                                className="text-[10px] w-fit shrink-0 font-normal border-transparent bg-amber-100 text-amber-800"
                                data-testid={`study-pending-deletion-${i}`}
                              >
                                Pending deletion
                              </Badge>
                            )}
                          </div>
                          {pendingDeletion && s.pending_deletion_by_name && (
                            <p className="text-[11px] text-muted-foreground truncate">
                              Requested by {s.pending_deletion_by_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="truncate" title={ctReviewer || undefined}>
                          {ctReviewer || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{s.study_date}</TableCell>
                      <TableCell className="tabular-nums">
                        {formOnly
                          ? "—"
                          : ((s.files_count ?? 0) > 0 ? s.files_count : (s.documents_count ?? 0))}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-0.5 items-center flex-nowrap">
                          {canPreviewStudy(s) && (
                            <button
                              type="button"
                              data-testid={`view-study-${i}`}
                              onClick={() => openStudy(s)}
                              className="p-2 hover:bg-secondary rounded-md"
                              title={formOnly ? "View measurements" : (isPdfStudy(s) ? "View report" : "View")}
                            >
                              <Eye className="w-4 h-4 text-muted-foreground" />
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`discuss-study-${i}`}
                            onClick={() => setDiscussionStudy({ ...s, _initialTab: "discussion" })}
                            className="p-2 hover:bg-secondary rounded-md"
                            title="Discussion"
                          >
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          </button>
                          {canShare && (
                            <button
                              type="button"
                              data-testid={`share-study-${i}`}
                              onClick={() => setShareStudy(s)}
                              className="p-2 hover:bg-secondary rounded-md"
                              title="Share"
                            >
                              <Share2 className="w-4 h-4 text-muted-foreground" />
                            </button>
                          )}
                          {canDownload && !formOnly && (
                            <button
                              type="button"
                              data-testid={`download-study-${i}`}
                              onClick={() => handleDownloadStudy(s)}
                              disabled={downloadingStudyId === s.id}
                              className="p-2 hover:bg-secondary rounded-md disabled:opacity-50"
                              title="Download all files"
                              aria-label={`Download all files for ${s.description || "study"}`}
                            >
                              {downloadingStudyId === s.id ? (
                                <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                              ) : (
                                <Download className="w-4 h-4 text-muted-foreground" />
                              )}
                            </button>
                          )}
                          {canManagePatients && !pendingDeletion && (
                            <button
                              type="button"
                              data-testid={`delete-study-${i}`}
                              onClick={() => setStudyToDelete(s)}
                              className="p-2 hover:bg-red-50 rounded-md"
                              title="Delete study"
                              aria-label={`Delete ${s.description || "study"}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col animate-fade-in animate-fade-in-delay-1 min-w-0 lg:col-span-4" data-testid="patient-timeline">
          <CardHeader className="pb-2 pt-4 shrink-0 min-h-[3.25rem] flex items-center">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" /> Timeline ({timelineLogs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 pt-0 pb-3 px-6 min-h-0">
            <div className={`overflow-auto ${STUDIES_PANEL_HEIGHT}`}>
              {timelineLoading ? (
                <div className="flex items-center justify-center h-32 text-sm text-muted-foreground gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading timeline...
                </div>
              ) : timelineLogs.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                  No activity recorded
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-[11px] top-0 bottom-0 w-px bg-border" />
                  {timelineLogs.map((log, i) => {
                    const ActionIcon = AUDIT_ACTION_ICONS[log.action] || Clock;
                    const actionColor = AUDIT_ACTION_COLORS[log.action] || "bg-secondary text-muted-foreground";
                    return (
                      <div key={log.id || `${log.timestamp}-${i}`} className="relative pl-8 pb-5 last:pb-0" data-testid={`audit-timeline-${i}`}>
                        <div className="absolute left-[5px] top-1 w-[13px] h-[13px] rounded-full border-2 border-primary bg-background z-10" />
                        <div className="bg-secondary/30 rounded-lg p-3 border border-transparent">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge className={`text-[10px] px-1.5 py-0 gap-1 border-transparent ${actionColor}`}>
                              <ActionIcon className="w-3 h-3" />
                              {log.action || "event"}
                            </Badge>
                            {log.entity_type ? (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                                {log.entity_type}
                              </Badge>
                            ) : null}
                          </div>
                          <p className="text-sm font-medium leading-snug mt-1.5">
                            {log.details || "—"}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatAuditTimestamp(log.timestamp)}
                            </span>
                            {log.user_name ? (
                              <span className="truncate">{log.user_name}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet
        open={addStudyOpen}
        onOpenChange={(open) => {
          setAddStudyOpen(open);
          if (!open) {
            setMismatchReviewHostOpen(false);
            setFinishAddStudyFileId(null);
            refreshPatient().catch(() => {});
          }
        }}
      >
        <SheetContent
          side="right"
          className="p-0 w-full sm:max-w-[900px]"
          data-testid="add-study-sheet"
        >
          <div className="h-full overflow-auto">
            {addStudyOpen ? (
              <UploadPage
                key={`add-study-${patient?.uhid ?? uhid}-${finishAddStudyFileId || "new"}`}
                embeddedUhid={patient?.uhid ?? uhid}
                embeddedPatient={addStudyPatient}
                hidePatientControls
                hideStudyControls
                lockPatientUhid
                autoUploadAfterExtract
                finishFileId={finishAddStudyFileId}
                onUploadCancelled={handleCloseAddStudyHost}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {/* Off-screen host: continue extract/auto-save after Add Study closes, and open
          tray Mismatch without showing the sheet. Avoid display:none / aria-hidden. */}
      {showHiddenAddStudyHost ? (
        <div
          className="pointer-events-none fixed left-0 top-0 z-[-1] h-0 w-0 overflow-hidden opacity-0"
          data-testid="mismatch-review-host"
        >
          <UploadPage
            key={`mismatch-review-${patient?.uhid ?? uhid}`}
            embeddedUhid={patient?.uhid ?? uhid}
            embeddedPatient={addStudyPatient}
            hidePatientControls
            hideStudyControls
            lockPatientUhid
            autoUploadAfterExtract
            mismatchReviewOnly={mismatchReviewHostOpen && !hasAddStudyBackgroundWork}
            onUploadCancelled={handleCloseAddStudyHost}
          />
        </div>
      ) : null}

      <Sheet open={!!discussionStudy} onOpenChange={(open) => { if (!open) setDiscussionStudy(null); }}>
        <SheetContent className="w-[460px] sm:w-[460px] p-0 flex flex-col" data-testid="study-sheet">
          {discussionStudy && (
            <Tabs defaultValue={discussionStudy._initialTab || "discussion"} className="flex flex-col h-full">
              <div className="px-5 pt-4 pb-2 border-b border-border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  {getModality(discussionStudy.modality)?.label ?? discussionStudy.modality} · {formatPatientDisplayUhid(patient)}
                </div>
                <div className="text-sm font-medium truncate" title={discussionStudy.description}>
                  {discussionStudy.description}
                </div>
                <TabsList className="mt-3 bg-secondary/40 h-9">
                  <TabsTrigger value="discussion" className="text-xs h-7" data-testid="tab-discussion">
                    Discussion
                  </TabsTrigger>
                  {/* <TabsTrigger value="reports" className="text-xs h-7" data-testid="tab-reports">
                    Reports
                  </TabsTrigger> */}
                </TabsList>
              </div>
              <TabsContent value="discussion" className="flex-1 m-0 overflow-hidden">
                <StudyDiscussion
                  studyId={discussionStudy.id}
                  studyDescription={discussionStudy.description}
                  compact
                />
              </TabsContent>
              {/* <TabsContent value="reports" className="flex-1 m-0 overflow-hidden">
                <StudyReportsList
                  studyId={discussionStudy.id}
                  onImportClick={() => setImportStudy(discussionStudy)}
                />
              </TabsContent> */}
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      <ImportReportDialog
        open={!!importStudy}
        onOpenChange={(v) => { if (!v) setImportStudy(null); }}
        studyId={importStudy?.id}
        studyDescription={importStudy?.description}
      />

      <ShareStudyDialog
        open={!!shareStudy}
        onOpenChange={(open) => { if (!open) setShareStudy(null); }}
        study={shareStudy}
      />

      <SharePatientDialog
        open={sharePatientOpen}
        onOpenChange={setSharePatientOpen}
        patient={patient}
      />

      <OfficeDocumentViewerModal
        open={!!documentPreviewFile}
        onOpenChange={(open) => { if (!open) setDocumentPreviewFile(null); }}
        file={documentPreviewFile}
      />

      <Dialog
        open={Boolean(remarksStudy)}
        onOpenChange={(open) => {
          if (savingRemarks) return;
          if (!open) setRemarksStudy(null);
        }}
      >
        <DialogContent
          className="max-w-lg w-[calc(100%-2rem)]"
          data-testid="edit-other-comment-dialog"
        >
          <DialogHeader>
            <DialogTitle>Comment</DialogTitle>
            <DialogDescription>
              {getModality(remarksStudy?.modality)?.label ?? "Other"} study
              {remarksStudy?.study_date ? ` · ${remarksStudy.study_date}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="other-study-remarks">Comment</Label>
            <Textarea
              id="other-study-remarks"
              data-testid="edit-other-comment-input"
              value={remarksDraft}
              onChange={(e) => setRemarksDraft(e.target.value)}
              placeholder="Enter comment"
              rows={4}
              className="min-h-[6rem] w-full resize-y"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={savingRemarks}
              onClick={() => setRemarksStudy(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="edit-other-comment-save"
              disabled={savingRemarks}
              onClick={handleSaveRemarks}
              className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
            >
              {savingRemarks ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(echoFormStudy)}
        onOpenChange={(open) => { if (!open) setEchoFormStudy(null); }}
      >
        <DialogContent
          className="max-w-lg w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto"
          data-testid="echo-measurements-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {getModality(echoFormStudy?.modality)?.label ?? echoFormStudy?.modality ?? "Echo"} measurements
            </DialogTitle>
            <DialogDescription>
              {echoFormStudy?.study_date ? `Study date ${echoFormStudy.study_date}` : "Form entry"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Measurement</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(echoFormStudy?.measurements ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-muted-foreground py-6">
                      No measurements recorded
                    </TableCell>
                  </TableRow>
                ) : (echoFormStudy?.measurements ?? []).map((item) => (
                  <TableRow key={item?.name}>
                    <TableCell className="text-sm">{item?.name ?? "—"}</TableCell>
                    <TableCell className="text-sm">{item?.value ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <EditPatientDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        patient={patient}
        uhid={patient?.uhid ?? uhid}
        onSaved={handlePatientSaved}
      />

      <AlertDialog open={deleteOpen} onOpenChange={(open) => { if (!deleting) setDeleteOpen(open); }}>
        <AlertDialogContent data-testid="delete-patient-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete patient?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-medium text-foreground">
                {patient?.name || formatPatientDisplayUhid(patient)}
              </span>
              {" "}({formatPatientDisplayUhid(patient)}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-patient-confirm"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!studyToDelete}
        onOpenChange={(open) => { if (!open && !deletingStudy) setStudyToDelete(null); }}
      >
        <AlertDialogContent data-testid="delete-study-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete study?</AlertDialogTitle>
            <AlertDialogDescription>
              {((studyToDelete?.files_count ?? 0) > 0 || (studyToDelete?.reports_count ?? 0) > 0 || (studyToDelete?.documents_count ?? 0) > 0)
                ? (
                  <>
                    This study has attached files/reports. Deletion will be queued for admin approval.
                  </>
                ) : (
                  <>This will permanently delete the study. This action cannot be undone.</>
                )}
              {" "}
              <span className="font-medium text-foreground">
                {studyToDelete?.description || studyToDelete?.id}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingStudy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDeleteStudy();
              }}
              disabled={deletingStudy}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-study-confirm"
            >
              {deletingStudy ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function patientInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function InfoField({ label, value, labelClassName, alwaysShow = false }) {
  if (!alwaysShow && isMissingValue(value)) return null;
  const display = isMissingValue(value) ? "—" : value;

  return (
    <div className="min-w-0 text-left">
      <p className={`text-[11px] font-medium uppercase tracking-wider ${labelClassName ?? "text-muted-foreground"}`}>
        {label}
      </p>
      <p className="text-sm font-semibold mt-1 break-words">{display}</p>
    </div>
  );
}

function ValveDetailRow({ label, value }) {
  const display = isMissingValue(value) ? "—" : value;

  return (
    <div className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
      <p className="text-[11px] font-medium uppercase tracking-wider text-white/55">{label}</p>
      <p className="text-sm font-semibold text-white truncate text-right" title={String(display)}>
        {display}
      </p>
    </div>
  );
}
